/**
 * Table cell renderers for the evaluation-set surface: the overall level + band
 * position, and the per-section competency mini-bars. Pure presentational —
 * fed the projected `perAxis` levels from the get-set payload.
 */

import type { EvaluationSetAxisMeta } from "@shared/labs/level-assessment-evaluation-set";
import { C, confidenceTone, DM, levelLabel, UI } from "../results/ui-tokens";

type PerAxis = Record<string, { level: number | null; confidence: number }>;

/** A short, stable abbreviation for a competency label (bar caption). */
function abbr(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return label.slice(0, 3);
}

/** Fraction (0–1) of a level within the model's range, for bar height / dot. */
function frac(level: number, levels: { min: number; max: number }): number {
  const span = Math.max(1, levels.max - levels.min);
  return Math.min(1, Math.max(0, (level - levels.min) / span));
}

/** Overall level label + a compact whisker on the L-range rail: the dot sits on
 *  the continuous mean and the bar spans the estimated variation (mean ± σ),
 *  mirroring the detail-view whisker in miniature. With no dispersion the band
 *  collapses to the lone dot; with no reading at all, only the rail shows. */
export function LevelCell({
  level,
  mean,
  stddev,
  levels,
  names,
}: {
  level: number | null;
  mean: number | null;
  stddev: number | null;
  levels: { min: number; max: number };
  names: Record<string, string>;
}) {
  // Center the dot on the continuous mean (the band's center), falling back to
  // the rounded level if the mean is absent. `frac` clamps to the rail ends, so
  // mean ± σ that overruns the range is pinned to L{min}/L{max}.
  const center = mean ?? level;
  const dotPct = center == null ? null : frac(center, levels) * 100;
  const hasBand = center != null && stddev != null && stddev > 0;
  const loPct = hasBand ? frac(center - stddev, levels) * 100 : 0;
  const hiPct = hasBand ? frac(center + stddev, levels) * 100 : 0;
  const bandTitle =
    center == null
      ? undefined
      : hasBand
        ? `Estimativa L${center.toFixed(1)} · faixa L${(center - stddev).toFixed(1)}–L${(center + stddev).toFixed(1)}`
        : `Estimativa L${center.toFixed(1)}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ ...DM, fontWeight: 700, fontSize: 20, lineHeight: 1, color: C.ink }}>
        {level == null ? "—" : `L${level}`}
      </span>
      <div style={{ position: "relative", width: 84, height: 8 }} title={bandTitle}>
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 3,
            height: 2,
            background: C.ink10,
            borderRadius: 999,
          }}
        />
        {hasBand && (
          <>
            <div
              style={{
                position: "absolute",
                top: 2.5,
                left: `${loPct}%`,
                width: `${hiPct - loPct}%`,
                height: 3,
                background: C.red,
                borderRadius: 999,
              }}
            />
            {[
              { k: "lo", p: loPct },
              { k: "hi", p: hiPct },
            ].map(({ k, p }) => (
              <div
                key={k}
                style={{
                  position: "absolute",
                  top: 0,
                  left: `calc(${p}% - 0.75px)`,
                  width: 1.5,
                  height: 8,
                  background: C.red,
                  borderRadius: 999,
                }}
              />
            ))}
          </>
        )}
        {dotPct != null && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: `calc(${dotPct}% - 4px)`,
              width: 8,
              height: 8,
              borderRadius: 999,
              background: C.red,
              border: "1.5px solid #fff",
              boxSizing: "border-box",
            }}
          />
        )}
      </div>
      <span style={{ ...UI, fontSize: 11, color: C.ink50 }} title={levelLabel(level, names)}>
        {level == null ? "sem nível" : levelLabel(level, names)}
      </span>
    </div>
  );
}

/** A row of per-competency mini-bars for one section (cross or vertical). */
export function MiniBars({
  axes,
  perAxis,
  levels,
}: {
  axes: EvaluationSetAxisMeta[];
  perAxis: PerAxis;
  levels: { min: number; max: number };
}) {
  if (axes.length === 0) {
    return <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>—</span>;
  }
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 9 }}>
      {axes.map((ax) => {
        const cell = perAxis[ax.axisId];
        const level = cell?.level ?? null;
        const tone = confidenceTone(cell?.confidence ?? 0);
        const h = level == null ? 0 : Math.round(6 + frac(level, levels) * 32);
        return (
          <div
            key={ax.axisId}
            title={`${ax.label}: ${level == null ? "sem nível" : `L${level}`}`}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}
          >
            <span style={{ ...UI, fontSize: 11, fontWeight: 600, color: C.ink70 }}>
              {level == null ? "—" : level}
            </span>
            <div style={{ height: 38, display: "flex", alignItems: "flex-end" }}>
              <div
                style={{
                  width: 12,
                  height: Math.max(2, h),
                  borderRadius: 4,
                  background: level == null ? C.ink10 : tone.fg,
                }}
              />
            </div>
            <span style={{ ...UI, fontSize: 10, color: C.ink50 }}>{abbr(ax.label)}</span>
          </div>
        );
      })}
    </div>
  );
}
